'use client';

import React, { ElementType, Fragment, useCallback, useMemo } from 'react';
import Link from 'next/link';

import {
  Button as ChakraButton,
  Circle,
  Flex,
  Stack,
  Text,
  useBreakpointValue,
  useSteps,
  useTheme
} from '@chakra-ui/react';

import { ShopCartStep } from 'types';
import {
  ProgressIndicatorConfig,
  ProgressIndicatorType,
  UniversalStep
} from 'types/common.types';

import { Button } from 'ui';
import {
  getBreakpointConfig,
  getStepMobileName,
  getStepNumber,
  getStepRoute,
  getStepTitle,
  renderIcon
} from './helpers';

// Base interface for steps
interface BaseProgressIndicatorProps {
  currentStep: number;
  onChangeStep?: (step: number) => void;
}

// Interface for Cart flow (backward compatibility)
interface CartProgressIndicatorProps extends BaseProgressIndicatorProps {
  steps: {
    step: ShopCartStep;
    title: string;
    icon: ElementType;
  }[];
  config?: never;
  universalSteps?: never;
}

// Interface for universal usage
interface UniversalProgressIndicatorProps extends BaseProgressIndicatorProps {
  universalSteps: UniversalStep[];
  config: ProgressIndicatorConfig;
  steps?: never;
  onChangeLocation?: () => void;
}

type CartProgressIndicatorComponentProps =
  | CartProgressIndicatorProps
  | UniversalProgressIndicatorProps;

const CartProgressIndicator = ({
  currentStep,
  onChangeStep,
  ...props
}: CartProgressIndicatorComponentProps) => {
  // Determine if this is universal mode
  const isUniversal = 'config' in props && props.config;

  const {
    colors: {
      palette: {
        components: { progress: themeColors }
      }
    }
  } = useTheme();

  // Get steps depending on mode
  const steps = isUniversal ? props.universalSteps : props.steps;
  const config = isUniversal
    ? { ...props.config, showNumbers: true, showIcons: false }
    : {
        type: ProgressIndicatorType.CART,
        showIcons: false,
        showNumbers: true,
        enableNavigation: true,
        useRouting: false,
        themePath: 'components.progress'
      };

  // Get the onChangeLocation handler for universal mode
  const onChangeLocation = isUniversal ? props.onChangeLocation : undefined;

  // Dynamic breakpoint based on steps count
  const breakpointConfig = useMemo(
    () => getBreakpointConfig(steps.length),
    [steps.length]
  );

  // Check if we should use mobile layout
  const isMobileLayout = useBreakpointValue(breakpointConfig);

  // Determine which breakpoint to use for stepTitle display
  const stepTitleBreakpoint = useMemo(() => {
    if (steps.length > 5) {
      return 'lg'; // Show titles only on screens >= 992px
    }
    return 'md'; // Default: show titles on screens >= 768px
  }, [steps.length]);

  const { setActiveStep } = useSteps({
    index: currentStep,
    count: steps.length
  });

  const handleChange = (step: number) => () => {
    setActiveStep(step);
    if (typeof onChangeStep === 'function') {
      onChangeStep(step);
    }
  };

  const getColor = useCallback(
    (idx: number) => {
      if (idx === currentStep) {
        return themeColors.stepIconCurrentColor || themeColors.activeColor;
      }
      if (idx < currentStep) {
        return themeColors.stepIconActiveColor || themeColors.completeColor;
      }

      return themeColors.stepIconInactiveColor || themeColors.incompleteColor;
    },
    [currentStep, themeColors]
  );

  const renderLocationSection = () => {
    if (!config.locationSection?.show) return null;

    return (
      <Stack
        alignItems={{ base: 'center', md: 'flex-start' }}
        flexDirection={{ base: 'row', md: 'column' }}
        justifyContent={{ base: 'space-between', md: 'center' }}
        w={{ base: '100%', md: '200px' }}
        gap='0'
      >
        <Text color={themeColors.locationNameColor} fontSize='18px'>
          {config.locationSection.locationName}
        </Text>
        {!config.locationSection.isSingleLocation && (
          <ChakraButton
            onClick={() => {
              if (onChangeLocation) {
                // Use specific location change handler if provided
                onChangeLocation();
              } else if (typeof onChangeStep === 'function') {
                // Fallback to step change with index 0
                onChangeStep(0);
              }
            }}
            variant='text'
            fontSize='11px'
            textTransform='uppercase'
            textDecoration='underline'
            color={themeColors.changeButtonColor}
            px={0}
            py='3px'
            minHeight='20px'
            height='auto'
          >
            Change
          </ChakraButton>
        )}
      </Stack>
    );
  };

  const renderStepContent = (step: unknown, index: number) => {
    const stepNumber = getStepNumber(step);
    const isClickable = stepNumber < currentStep && config.enableNavigation;
    const isFutureStep = stepNumber > currentStep;

    // Determine which title to show (mobile or desktop)
    const mobileName = getStepMobileName(step);
    const stepTitle =
      isMobileLayout && mobileName ? mobileName : getStepTitle(step);

    // For Link navigation use separate render
    const stepRoute = getStepRoute(step);
    if (config.useRouting && stepRoute) {
      // Don't render as Link if it's a future step
      if (isFutureStep) {
        return (
          <Flex
            align='center'
            cursor='not-allowed'
            opacity={1}
            margin={{ base: '0 2px', md: '0 5px' }}
          >
            {config.showNumbers && (
              <Circle
                size={{ base: '26px', sm: '32px' }}
                borderColor={getColor(stepNumber)}
                border='1px solid'
                color={getColor(stepNumber)}
                bg='transparent'
                fontWeight='bold'
                position='relative'
                zIndex={1}
              >
                {index + 1}
              </Circle>
            )}

            {renderIcon(
              step,
              stepNumber,
              getColor,
              config.showIcons,
              config.showNumbers
            )}

            <Text
              ml={config.showNumbers ? 2 : 0}
              display={{
                base: stepNumber === currentStep ? 'block' : 'none',
                [stepTitleBreakpoint]: 'block'
              }}
              color={getColor(stepNumber)}
              textTransform='uppercase'
              fontSize={{
                base: '13px',
                md: '12px',
                lg: '14px'
              }}
              whiteSpace='nowrap'
              lineHeight={1}
            >
              {stepTitle}
            </Text>
          </Flex>
        );
      }

      return (
        <Link
          href={stepRoute}
          style={{ textDecoration: 'none', margin: '0 2px' }}
        >
          <Flex
            align='center'
            cursor={isClickable ? 'pointer' : 'default'}
            opacity={1}
            _hover={{ opacity: isClickable ? 0.7 : 1 }}
          >
            {config.showNumbers && (
              <Circle
                size={{ base: '26px', sm: '32px' }}
                borderColor={getColor(stepNumber)}
                border='1px solid'
                color={
                  stepNumber < currentStep
                    ? themeColors.background
                    : getColor(stepNumber)
                }
                bg={
                  stepNumber < currentStep
                    ? themeColors.stepIconActiveColor ||
                      themeColors.completeColor
                    : 'transparent'
                }
                fontWeight='bold'
                position='relative'
                zIndex={1}
              >
                {index + 1}
              </Circle>
            )}

            {renderIcon(
              step,
              stepNumber,
              getColor,
              config.showIcons,
              config.showNumbers
            )}

            <Text
              ml={config.showNumbers ? 2 : 0}
              display={{
                base: stepNumber === currentStep ? 'block' : 'none',
                [stepTitleBreakpoint]: 'block'
              }}
              color={getColor(stepNumber)}
              textTransform='uppercase'
              fontSize={{
                base: '13px',
                md: '12px',
                lg: '14px'
              }}
              whiteSpace='nowrap'
              lineHeight={1}
            >
              {stepTitle}
            </Text>
          </Flex>
        </Link>
      );
    }

    // For onClick navigation
    return (
      <Flex
        as={Button}
        minH='auto'
        minW='auto'
        variant='text'
        w='auto'
        p={0}
        align='center'
        onClick={handleChange(stepNumber)}
        cursor={isClickable ? 'pointer' : 'default'}
        _disabled={{
          opacity: 1
        }}
        _hover={{ opacity: isClickable ? 0.7 : 1 }}
        opacity={1}
        isDisabled={stepNumber > currentStep}
        margin='0 5px'
      >
        {config.showNumbers && (
          <Circle
            size={{ base: '26px', sm: '32px' }}
            borderColor={getColor(stepNumber)}
            border='1px solid'
            color={
              stepNumber < currentStep
                ? themeColors.background
                : getColor(stepNumber)
            }
            bg={
              stepNumber < currentStep
                ? themeColors.stepIconActiveColor || themeColors.completeColor
                : 'transparent'
            }
            fontWeight='bold'
            position='relative'
            zIndex={1}
          >
            {index + 1}
          </Circle>
        )}

        {renderIcon(
          step,
          stepNumber,
          getColor,
          config.showIcons,
          config.showNumbers
        )}

        <Text
          ml={config.showNumbers ? 2 : 0}
          display={{
            base: stepNumber === currentStep ? 'block' : 'none',
            [stepTitleBreakpoint]: 'block'
          }}
          color={getColor(stepNumber)}
          textTransform='uppercase'
          fontSize={{
            base: '13px',
            md: '12px',
            lg: '14px'
          }}
          whiteSpace='nowrap'
          lineHeight={1}
        >
          {stepTitle}
        </Text>
      </Flex>
    );
  };

  // For Cart flow hide entire component on SUCCESS step
  const shouldHideForCart =
    !isUniversal && currentStep === ShopCartStep.SUCCESS;

  return shouldHideForCart ? null : (
    <Flex
      w='full'
      // gap={{ base: '16px', md: '30px' }}
      bg={themeColors.background}
      px={{ base: '12px', md: '20px' }}
      py={{ base: '12px', md: '16px' }}
      borderRadius={{ base: 'none', sm: '6px' }}
      align='center'
      justify='space-between'
      flexWrap={{ base: 'wrap', md: 'nowrap' }}
      width={{ base: 'calc(100% + 30px)', sm: '100%' }}
      mx={{ base: '-15px', sm: '0' }}
    >
      {renderLocationSection()}

      {steps.map((step, index) => (
        <Fragment key={step.title || step.step}>
          <Flex
            display={{
              base:
                step.step === currentStep && step.step === steps.length - 1
                  ? 'flex'
                  : 'none',
              md: 'none'
            }}
            flex='1'
            h='1px'
            bg={getColor(step.step)}
            w='100%'
            margin='0 10px'
          />

          {renderStepContent(step, index)}

          <Flex
            display={{
              base:
                step.step === currentStep && step.step < steps.length - 1
                  ? 'flex'
                  : 'none',
              md: index < steps.length - 1 ? 'flex' : 'none'
            }}
            flex='1'
            // minW='20px'
            h='1px'
            bg={getColor(step.step)}
            w='100%'
            margin='0 10px'
          />
        </Fragment>
      ))}
    </Flex>
  );
};

export default CartProgressIndicator;
